import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import MuiGrid from '@material-ui/core/Grid';
import MuiBox from '@material-ui/core/Box';
import MuiButton from '@material-ui/core/Button';
import { withStyles } from '@material-ui/core/styles';
import MuiCircularProgress from '@material-ui/core/CircularProgress';
import MuiFormControl from '@material-ui/core/FormControl';
import MuiFormHelperText from '@material-ui/core/FormHelperText';
import MuiSelect from '@material-ui/core/Select';
import MuiMenuItem from '@material-ui/core/MenuItem';
import MuiTextField from '@material-ui/core/TextField';

import ENUMS from '../../constants/ENUMS.gen.json';

import * as API from 'api';

const CoManagerDocuments = API.CoManagerDocuments();

const styles = theme => ({
  bold: {
    fontWeight: 'bold',
  },
  formElement: {
    margin: '6px',
    padding: '6px',
    width: '100%',
  },
  checkBoxElement: {
    margin: '3px',
    padding: '3px',
  },
  toggleLabel: {
    margin: '0.5rem',
  },
  form: {
    width: '100%',
  },
  error: {
    color: theme.palette.primary.error,
  },
});

function DocumentForm({
  classes,
  selectedContentType,
  setSelectedContentType,
  question,
  setQuestion,
  answer,
  setAnswer,
  source,
  setSource,
}) {
  return (
    <MuiGrid container>
      <MuiGrid item xs={3}>
        <span className={classes.bold}>Training Details</span>
        <p>Add the details of the training entry. These details can be edited later.</p>
        <p>
          The question should contain as much context as necessary in order to avoid matching on unrelated questions.
        </p>
        <p>The answer should contain enough context to stand alone without the question.</p>
        <p>
          Markdown is allowed in the answer (but it won't be rendered as markdown here).{' '}
          <a href="https://www.markdownguide.org/cheat-sheet/">Formatting instructions</a>
        </p>
      </MuiGrid>
      <MuiGrid item xs={6}>
        <MuiBox>
          <MuiFormControl className={classes.formElement}>
            <MuiFormHelperText>Training Type</MuiFormHelperText>
            <>
              <MuiSelect
                labelId="select-deal-type"
                id="select-deal-type"
                value={selectedContentType}
                onChange={evt => {
                  setSelectedContentType(evt.target.value);
                }}>
                <MuiMenuItem value={0}> -- Please Select A Training Type -- </MuiMenuItem>
                {Object.values(ENUMS.COMANAGER_DOCUMENT_TYPE)?.map(dealType => (
                  <MuiMenuItem key={dealType} value={dealType}>
                    {dealType}
                  </MuiMenuItem>
                ))}
              </MuiSelect>
            </>
          </MuiFormControl>
        </MuiBox>
        <MuiBox>
          <MuiFormControl className={classes.formElement}>
            <MuiFormHelperText>Question</MuiFormHelperText>
            <MuiTextField
              onChange={e => {
                setQuestion(e.target.value);
              }}
              value={question}
              multiline={true}
              minRows={2}
            />
          </MuiFormControl>
        </MuiBox>

        <MuiBox>
          <MuiFormControl className={classes.formElement}>
            <MuiFormHelperText>Answer</MuiFormHelperText>
            <MuiTextField
              inputProps={{
                maxLength: 10000,
              }}
              onChange={e => {
                setAnswer(e.target.value);
              }}
              value={answer}
              multiline={true}
              minRows={4}
            />
          </MuiFormControl>
        </MuiBox>

        <MuiBox>
          <MuiFormControl className={classes.formElement}>
            <MuiFormHelperText>Source (link to original info)</MuiFormHelperText>
            <MuiTextField
              onChange={e => {
                setSource(e.target.value);
              }}
              value={source}
              placeholder='e.g. "https://www.example.com"'
            />
          </MuiFormControl>
        </MuiBox>
      </MuiGrid>
    </MuiGrid>
  );
}

const StyledDealForm = withStyles(styles)(DocumentForm);

function CoManagerDocumentForm(props) {
  const { goBack, id, classes, setToast } = props;

  const [selectedContentType, setSelectedContentType] = useState('');
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState('');
  const [source, setSource] = useState(null);
  const queryClient = useQueryClient();

  const { isLoading: CoManagerDocLoading, data: CoManagerDocData } = useQuery(
    ['comanager-document', id],
    () => CoManagerDocuments.getById(id),
    {
      enabled: !!id,
      refetchOnWindowFocus: false,
    }
  );

  useEffect(() => {
    setSelectedContentType(CoManagerDocData?.content_type || '');
    setQuestion(CoManagerDocData?.question || '');
    setAnswer(CoManagerDocData?.answer || '');
    setSource(CoManagerDocData?.source || '');
  }, [CoManagerDocData]);

  const coManagerDocMutation = useMutation(
    body => (id ? CoManagerDocuments.update(id, body) : CoManagerDocuments.add(body)),
    {
      onSuccess: () => {
        setToast({ message: 'Success!', alertType: 'success' });
        setTimeout(() => {
          setToast({});
          goBack();
        }, 1500);
        queryClient.invalidateQueries('comanager-documents');
        queryClient.invalidateQueries(['comanager-document', id]);
      },
      onError: e => {
        setToast({ message: e?.response?.data?.detail || e?.response?.data || 'Error' });
      },
    }
  );

  if (CoManagerDocLoading || coManagerDocMutation.isLoading) {
    return <MuiCircularProgress />;
  }

  const handleOnSubmit = e => {
    e.preventDefault();

    const mutateBody = {
      question,
      answer,
      source,
      content_type: selectedContentType,
    };

    coManagerDocMutation.mutate(mutateBody);
  };

  const isValid = question && answer && selectedContentType;

  return (
    <form onSubmit={handleOnSubmit} className={classes.form}>
      <StyledDealForm
        comanagerDocumentId={parseInt(id, 10)}
        selectedContentType={selectedContentType}
        setSelectedContentType={setSelectedContentType}
        question={question}
        setQuestion={setQuestion}
        answer={answer}
        setAnswer={setAnswer}
        source={source}
        setSource={setSource}
      />
      <MuiGrid item xs={12}>
        <MuiBox>
          <MuiButton onClick={goBack}>Cancel</MuiButton>
          <MuiButton variant="contained" size="small" type="submit" disabled={!isValid}>
            Save
          </MuiButton>
        </MuiBox>
      </MuiGrid>
    </form>
  );
}

export default withStyles(styles)(CoManagerDocumentForm);
